// Pure schedule-editing rules (Task 6), kept out of the components so
// they're testable without mounting React or FullCalendar.
import { ApiError } from "../api/client.js";
import type { AdminLocation, AdminProvider, ScheduleShift } from "../api/types.js";

// Locations selectable for a shift, given the provider's type: a
// virtual_only provider can only be scheduled at the virtual location;
// every other provider can be scheduled at any active location (in-person
// or virtual — behavior 6, virtual overlap is always allowed).
export function locationsForProvider(
  locations: readonly AdminLocation[],
  provider: AdminProvider | undefined,
): AdminLocation[] {
  const active = locations.filter((l) => l.active);
  if (provider?.type === "virtual_only") {
    return active.filter((l) => l.kind === "virtual");
  }
  return active;
}

// Client-side courtesy check backing the API's second-physical-location
// guard (`SecondPhysicalLocationError` / 409 `second_location_conflict`):
// a provider can't have two *different* in-person locations on the same
// day. Checked against the already-loaded shifts before a round trip, so
// the popover can disable Save inline; the API 409 is the backstop for a
// race (another admin books the second location between page load and
// Save). Virtual locations never conflict with anything.
export function findSecondLocationConflict(
  shifts: readonly ScheduleShift[],
  locationById: ReadonlyMap<string, AdminLocation>,
  params: {
    providerId: string;
    date: string;
    locationId: string;
    // Exclude the shift being edited from its own conflict check.
    excludeShiftId?: string;
  },
): AdminLocation | null {
  const chosen = locationById.get(params.locationId);
  if (!chosen || chosen.kind !== "in_person") return null;
  for (const s of shifts) {
    if (s.id === params.excludeShiftId) continue;
    if (s.provider_id !== params.providerId) continue;
    if (s.shift_date !== params.date) continue;
    if (s.location_id === params.locationId) continue;
    const loc = locationById.get(s.location_id);
    if (loc && loc.kind === "in_person") return loc;
  }
  return null;
}

// A raw zod validation error, unmapped by the API's error-code switch,
// otherwise reaches the UI as literal serialized JSON (zod v3's
// `parse.error.message` is `JSON.stringify(issues, null, 2)` — starts with
// "[" and/or contains `"code":"invalid_string"` etc.). Shared by
// scheduleErrorMessage's default branch and LocationsPage's mutation-error
// rendering so both surfaces scrub it the same way.
export function humanizeErrorMessage(message: string | undefined): string {
  const fallback = "That didn't save — please check the form values.";
  if (!message) return fallback;
  const trimmed = message.trim();
  if (trimmed.startsWith("[") || trimmed.includes('"code":')) return fallback;
  return message;
}

// Shared error-to-message mapping for schedule-editing failures — used by
// both the popover's inline error (Save/Delete) and the calendar's
// drag-move/resize error banner, so the same API error code always reads
// the same way regardless of which UI surface hit it.
export function scheduleErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "second_location_conflict":
        // Backstop for findSecondLocationConflict's inline check — the API
        // doesn't send back which location it collided with, so this can't
        // name it the way the client-side check can.
        return "Already booked at a different location that day.";
      case "provider_not_in_person":
        return "This provider is virtual-only and can't be scheduled at an in-person location.";
      case "location_inactive":
        return "This location is inactive.";
      case "provider_inactive":
        return "This provider is inactive.";
      case "not_found":
        return "This entry no longer exists — it may have been changed elsewhere.";
      default:
        return err.message
          ? humanizeErrorMessage(err.message)
          : "Could not save. Try again.";
    }
  }
  return "Could not save. Try again.";
}
